// AI Step 1: System Identification
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Bot } from 'lucide-react';
import { AI_TECHNOLOGY_TYPES } from '../constants';

export default function Step1SystemInfo({ formData, updateField, entities }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">AI System Identification</h2>
        <p className="text-muted-foreground mt-1">Identify and describe the AI system being assessed.</p>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Bot className="h-4 w-4 text-primary" />System Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Entity <span className="text-destructive">*</span></Label>
              <Select value={formData.organization_id} onValueChange={v => updateField('organization_id', v)}>
                <SelectTrigger className="mt-1.5" data-testid="ai-entity-select"><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>{entities.map(e => <SelectItem key={e.id} value={e.id}>{e.entity_name} {e.client_name ? `(${e.client_name})` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>System Name <span className="text-destructive">*</span></Label>
              <Input value={formData.system_name} onChange={e => updateField('system_name', e.target.value)} placeholder="e.g., Customer Service Chatbot" className="mt-1.5" data-testid="ai-system-name" />
            </div>
          </div>
          <div>
            <Label>System Description</Label>
            <Textarea value={formData.system_description || ''} onChange={e => updateField('system_description', e.target.value)} placeholder="Describe what the AI system does..." className="mt-1.5" rows={3} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Vendor/Developer</Label>
              <Input value={formData.vendor_developer || ''} onChange={e => updateField('vendor_developer', e.target.value)} placeholder="e.g., OpenAI, In-house" className="mt-1.5" />
            </div>
            <div>
              <Label>AI Technology Type</Label>
              <Select value={formData.ai_technology_type} onValueChange={v => updateField('ai_technology_type', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{AI_TECHNOLOGY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Deployment Type</Label>
              <Select value={formData.deployment_type} onValueChange={v => updateField('deployment_type', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Use</SelectItem>
                  <SelectItem value="external">External/Customer-Facing</SelectItem>
                  <SelectItem value="embedded">Embedded in Product</SelectItem>
                  <SelectItem value="saas">SaaS/Cloud Service</SelectItem>
                  <SelectItem value="on_premise">On-Premise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lifecycle Stage</Label>
              <Select value={formData.lifecycle_stage} onValueChange={v => updateField('lifecycle_stage', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="development">Development</SelectItem>
                  <SelectItem value="testing">Testing/Evaluation</SelectItem>
                  <SelectItem value="pilot">Pilot/Limited Deployment</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="decommissioning">Decommissioning</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
